import React, { useState } from 'react';
import { User, Mail, Phone, Building, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from './ui/Button';

interface AddClientFormProps {
  onSuccess: (client: any) => void;
  onCancel: () => void;
}

const AddClientForm: React.FC<AddClientFormProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  // Real-time validation
  const validateField = (field: string, value: string) => {
    const errors = { ...fieldErrors };
    
    switch (field) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Le nom est requis';
        } else if (value.trim().length < 2) {
          errors.name = 'Le nom doit contenir au moins 2 caractères';
        } else {
          delete errors.name;
        }
        break;
      case 'email':
        if (!value.trim()) {
          errors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Veuillez entrer une adresse email valide';
        } else {
          delete errors.email;
        }
        break;
      case 'phone':
        if (!value.trim()) {
          errors.phone = 'Le téléphone est requis';
        } else if (!/^[\+]?[0-9\s\-\(\)]{8,}$/.test(value.trim())) {
          errors.phone = 'Veuillez entrer un numéro de téléphone valide';
        } else {
          delete errors.phone;
        }
        break;
      case 'company_name':
        if (!value.trim()) {
          errors.company_name = 'L\'entreprise est requise';
        } else if (value.trim().length < 2) {
          errors.company_name = 'Le nom de l\'entreprise doit contenir au moins 2 caractères';
        } else {
          delete errors.company_name;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    Object.keys(formData).forEach(field => {
      validateField(field, formData[field as keyof typeof formData]);
    });
    
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Import the createClient function
      const { createClient } = await import('../lib/api');
      
      // Create the client
      const newClient = await createClient(formData);
      
      // Call success callback
      onSuccess(newClient);
      
    } catch (err: any) {
      console.error('Error creating client:', err);
      if (err.message?.includes('duplicate key value violates unique constraint')) {
        setError('Un client avec cet email existe déjà');
      } else {
        setError(err.message || 'Erreur lors de la création du client');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.company_name && Object.keys(fieldErrors).length === 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center"
        >
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="h-4 w-4 inline mr-1" />
            Nom du contact *
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.name 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
            placeholder="Nom et prénom du contact principal"
          />
          {fieldErrors.name && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.name}
            </motion.p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="h-4 w-4 inline mr-1" />
            Adresse email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.email 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
            placeholder="contact@entreprise.com"
          />
          {fieldErrors.email && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.email}
            </motion.p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="h-4 w-4 inline mr-1" />
            Téléphone *
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.phone 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
            placeholder="+33 1 23 45 67 89"
          />
          {fieldErrors.phone && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.phone}
            </motion.p>
          )}
        </div>

        {/* Company Field */}
        <div>
          <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="h-4 w-4 inline mr-1" />
            Entreprise *
          </label>
          <input
            id="company_name"
            name="company_name"
            type="text"
            required
            value={formData.company_name}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.company_name 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
            placeholder="Nom de l'entreprise"
          />
          {fieldErrors.company_name && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.company_name}
            </motion.p>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          icon={isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        >
          {isSubmitting ? 'Création en cours...' : 'Créer le client'}
        </Button>
      </div>
    </form>
  );
};

export default AddClientForm;